// In production REACT_APP_API is "" so calls go to the same CloudFront origin
// (which proxies /api/* to the API). Unset -> local dev API.
const API = process.env.REACT_APP_API ?? "http://localhost:3001";

// The Cognito ID token, set by App once the user is authenticated.
let authToken = null;
export function setAuthToken(t) { authToken = t; }
const authHeaders = () => (authToken ? { Authorization: `Bearer ${authToken}` } : {});

async function get(path) {
  const r = await fetch(`${API}${path}`, { headers: authHeaders() });
  if (!r.ok) throw new Error(`${r.status} ${path}`);
  return r.json();
}

async function send(method, path, body) {
  const r = await fetch(`${API}${path}`, {
    method,
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  if (!r.ok) throw new Error(`${r.status} ${path}`);
  return r.json();
}

export const getSummary = () => get("/api/jobs/summary");
export const getMe = () => get("/api/me");
// Admin only (platform-wide).
export const getAdminOverview = () => get("/api/admin/overview");
export const getAdminActivity = () => get("/api/admin/activity");
export const getQueryEffectiveness = () => get("/api/admin/query-effectiveness");
export const getJobs = (status) =>
  get(`/api/jobs${status && status !== "all" ? `?status=${encodeURIComponent(status)}` : ""}`);
// Every role from one company, across all statuses (issue #5).
export const getJobsByCompany = (company) =>
  get(`/api/jobs?company=${encodeURIComponent(company)}`);
export const getJob = (id) => get(`/api/jobs/${id}`);
// Transparency: the account-wide activity feed + a single job's timeline.
export const getActivityFeed = (limit) => get(`/api/activity-feed${limit ? `?limit=${limit}` : ""}`);
export const getJobTimeline = (id) => get(`/api/jobs/${id}/timeline`);
// This tenant's email ingest address (forward job alerts here).
export const getIngestAddress = () => get("/api/ingest-address");
export const getSearchConfig = () => get("/api/search-config");
export const updateSearchConfig = (patch) => send("PUT", "/api/search-config", patch);
export const getApplicationFacts = () => get("/api/application-facts");
export const updateApplicationFacts = (facts) => send("PUT", "/api/application-facts", facts);

// Learned preferences (distilled from the seeker's own edits/skips/comments).
export const getDistilled = () => get("/api/distilled");
export const runDistill = () => send("POST", "/api/distill-async", {});
export const getTask = (id) => get(`/api/tasks/${id}`);

export async function generateDocs(id, feedback) {
  const r = await fetch(`${API}/api/jobs/${id}/generate`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(feedback ? { feedback } : {}),
  });
  if (!r.ok) throw new Error(`generate failed ${r.status}`);
  return r.json();
}

export const getGenerated = (id) => get(`/api/jobs/${id}/generated`);

// Review loop.
export const setJobStatus = (id, status, reason) => send("PATCH", `/api/jobs/${id}/status`, { status, reason });
export const updateApplicationStatus = (id, applicationStatus) => send("PATCH", `/api/jobs/${id}/application-status`, { applicationStatus });
export const updateApplicationNotes = (id, notes) => send("PATCH", `/api/jobs/${id}/application-notes`, { notes });
export const addComment = (id, body, target, quote) => send("POST", `/api/jobs/${id}/comments`, { body, target, quote });
export const saveGenerated = (id, docs) => send("PUT", `/api/jobs/${id}/generated`, docs);
// Edit history.
export const getVersions = (id) => get(`/api/jobs/${id}/generated/versions`);
export const getVersion = (id, vid) => get(`/api/jobs/${id}/generated/versions/${vid}`);
export const restoreVersion = (id, vid) => send("POST", `/api/jobs/${id}/generated/versions/${vid}/restore`, {});

// Open the print-ready HTML in a new tab. We fetch it with the auth header (a
// plain link can't send one), then point the tab at a blob URL. The window is
// opened synchronously in the click gesture to dodge popup blockers.
export async function openGeneratedDoc(id, kind) {
  const w = window.open("", "_blank");
  try {
    const r = await fetch(`${API}/api/jobs/${id}/generated/${kind}.html`, { headers: authHeaders() });
    if (!r.ok) throw new Error(`${r.status}`);
    const html = await r.text();
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    if (w) w.location = url; else window.open(url, "_blank");
  } catch (e) {
    if (w) w.close();
    throw e;
  }
}
